import { useRef, type KeyboardEvent } from "react";

const DASHBOARD_TABS = [
  { id: "overview", label: "Overview" },
  { id: "spread", label: "Spread Monitor / Historical" },
  { id: "seasonality", label: "Seasonality" },
  { id: "signals", label: "Signal Lab / Backtest" },
  { id: "regime", label: "Regime / Research" },
] as const;

export type DashboardTabId = (typeof DASHBOARD_TABS)[number]["id"];

interface DashboardTabsProps {
  readonly activeTab: DashboardTabId;
  readonly onChange: (tab: DashboardTabId) => void;
}

export function DashboardTabs({ activeTab, onChange }: DashboardTabsProps) {
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const activateAt = (index: number) => {
    const normalized = (index + DASHBOARD_TABS.length) % DASHBOARD_TABS.length;
    const tab = DASHBOARD_TABS[normalized];
    if (!tab) return;
    onChange(tab.id);
    tabRefs.current[normalized]?.focus();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (event.key === "ArrowRight") {
      event.preventDefault();
      activateAt(index + 1);
    } else if (event.key === "ArrowLeft") {
      event.preventDefault();
      activateAt(index - 1);
    } else if (event.key === "Home") {
      event.preventDefault();
      activateAt(0);
    } else if (event.key === "End") {
      event.preventDefault();
      activateAt(DASHBOARD_TABS.length - 1);
    }
  };

  return (
    <nav aria-label="Dashboard sections" className="scrollbar-none overflow-x-auto border-b border-divider-subtle">
      <div className="flex min-w-max gap-2" role="tablist" aria-orientation="horizontal">
        {DASHBOARD_TABS.map((tab, index) => {
          const selected = tab.id === activeTab;
          return (
            <button
              key={tab.id}
              ref={(node) => { tabRefs.current[index] = node; }}
              id={`tab-${tab.id}`}
              type="button"
              role="tab"
              aria-selected={selected}
              aria-controls={`panel-${tab.id}`}
              tabIndex={selected ? 0 : -1}
              onClick={() => onChange(tab.id)}
              onKeyDown={(event) => handleKeyDown(event, index)}
              className={`shrink-0 border-b-2 px-4 py-3 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent ${
                selected ? "border-accent text-accent" : "border-transparent text-muted hover:text-primary"
              }`}
            >
              {tab.label}
            </button>
          );
        })}
      </div>
    </nav>
  );
}
